// Sopwith 3D - CGA-style software framebuffer.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.
//
// A 320x200 buffer of 2-bit colour indices, drawn into the way the original
// drew into CGA memory: sprites are XORed on, and the ground is a one-pixel
// line. World Y grows upward, so row 0 of the buffer is world y = 199. This
// module has no DOM dependency so it can be unit tested; a presenter copies
// it to a canvas.

import { SCR_HGHT, SCR_WDTH } from '../sim/constants';
import type { Sopsym } from '../sim/types';

export interface Palette {
  name: string;
  colors: [number, number, number][];
}

/** Monitor palettes, in the same order SDL Sopwith offers them. */
export const PALETTES: Palette[] = [
  {
    name: 'CGA 1',
    colors: [
      [0, 0, 0],
      [0, 255, 255],
      [255, 0, 255],
      [255, 255, 255],
    ],
  },
  {
    name: 'CGA 2',
    colors: [
      [0, 0, 0],
      [0, 255, 0],
      [255, 0, 0],
      [255, 255, 0],
    ],
  },
  {
    name: 'CGA 3',
    colors: [
      [0, 0, 0],
      [0, 255, 255],
      [255, 0, 0],
      [255, 255, 255],
    ],
  },
  {
    name: 'Mono Amber',
    colors: [
      [0, 0, 0],
      [255, 170, 16],
      [242, 125, 0],
      [255, 226, 52],
    ],
  },
  {
    name: 'Mono Green',
    colors: [
      [0, 0, 0],
      [12, 238, 56],
      [8, 202, 48],
      [49, 253, 90],
    ],
  },
  {
    name: 'Mono Grey',
    colors: [
      [0, 0, 0],
      [222, 222, 210],
      [182, 186, 182],
      [255, 255, 255],
    ],
  },
];

/**
 * How each faction's sprites map the four sprite colour indices onto screen
 * colours: index 1 is the fuselage, 2 the wings, 3 detail. Indexed by `clr`.
 */
export const COLOR_MAPPINGS: readonly (readonly number[])[] = [
  [0, 3, 3, 3], // all white: NONE
  [0, 1, 2, 3], // cyan fuselage, magenta wings: PLAYER1
  [0, 2, 1, 3], // magenta fuselage, cyan wings: PLAYER2
  [0, 1, 3, 2], // PLAYER3
  [0, 2, 3, 1], // PLAYER4
  [0, 3, 1, 2], // PLAYER5
  [0, 3, 2, 1], // PLAYER6
  [0, 1, 1, 3], // PLAYER7
  [0, 2, 2, 3], // PLAYER8
];

export function fuselageColor(clr: number): number {
  return COLOR_MAPPINGS[clr][1];
}

export class Framebuffer {
  readonly width = SCR_WDTH;
  readonly height = SCR_HGHT;
  /** One colour index (0..3) per pixel, row 0 at the top of the screen. */
  readonly vram = new Uint8Array(SCR_WDTH * SCR_HGHT);

  clear(): void {
    this.vram.fill(0);
  }

  /** Buffer offset of world coordinate (x, y). No bounds check. */
  private idx(x: number, y: number): number {
    return (this.height - 1 - y) * this.width + x;
  }

  private inBounds(x: number, y: number): boolean {
    return x >= 0 && x < this.width && y >= 0 && y < this.height;
  }

  get(x: number, y: number): number {
    return this.inBounds(x, y) ? this.vram[this.idx(x, y)] : 0;
  }

  plotPixel(x: number, y: number, clr: number): void {
    if (this.inBounds(x, y)) {
      this.vram[this.idx(x, y)] = clr & 3;
    }
  }

  xorPixel(x: number, y: number, clr: number): void {
    if (this.inBounds(x, y)) {
      this.vram[this.idx(x, y)] ^= clr & 3;
    }
  }

  /** Fill a box whose top-left world corner is (x, y), `h + 1` rows tall. */
  box(x: number, y: number, w: number, h: number, c: number): void {
    for (let row = 0; row <= h; row++) {
      const yy = y - row;
      if (yy < 0 || yy >= this.height) continue;
      const start = this.idx(0, yy);
      const x0 = Math.max(0, x);
      const x1 = Math.min(this.width, x + w);
      if (x1 > x0) {
        this.vram.fill(c & 3, start + x0, start + x1);
      }
    }
  }

  /**
   * XOR a sprite onto the buffer with its top-left at world (x, y), colours
   * remapped for faction `clr`. Clipped at every edge.
   */
  dispSymbol(x: number, y: number, sym: Sopsym, clr: number): void {
    if (sym.w === 1 && sym.h === 1) {
      this.xorPixel(x, y, clr);
      return;
    }
    const map = COLOR_MAPPINGS[clr] ?? COLOR_MAPPINGS[0];
    const leftSkip = x < 0 ? -x : 0;
    let w = sym.w;
    if (x + w > this.width) {
      w -= x + w - this.width;
    }
    let h = sym.h;
    if (h > y + 1) {
      h = y + 1;
    }
    for (let y1 = 0; y1 < h; y1++) {
      const wy = y - y1;
      if (wy >= this.height) continue;
      const row = this.idx(0, wy);
      const src = y1 * sym.w;
      for (let x1 = leftSkip; x1 < w; x1++) {
        const i = sym.data[src + x1];
        if (i) {
          this.vram[row + x + x1] ^= map[i];
        }
      }
    }
  }

  /**
   * Draw `w` columns of terrain as a one-pixel line, starting at world
   * column `xstart` on screen and reading heights from `ground[offset..]`.
   * Uses the original's three-column window so cliffs and pillars connect.
   */
  dispGround(ground: Uint8Array, offset: number, xstart: number, w: number): void {
    const top = this.height - 1;
    let hc = Math.min(ground[offset], top);
    let hl = hc;
    let y = top;
    for (let x = 0; x < w - 1; x++) {
      const hr = Math.min(ground[offset + x + 1], top);
      if (y > hl) y = hl;
      if (y > hr) y = hr;
      if (y >= hc) y = hc - 1;
      while (y < hc) {
        y++;
        this.xorPixel(xstart + x, y, 3);
      }
      hl = hc;
      hc = hr;
    }
    this.xorPixel(xstart + w - 1, hc, 3);
  }

  /** Solid terrain fill (the Sopwith 1 look), leaving the status bar clear. */
  dispGroundSolid(ground: Uint8Array, offset: number, xstart: number, w: number, sbar: number): void {
    for (let x = 0; x < w; x++) {
      const gc = Math.min(ground[offset + x], this.height - 1);
      for (let y = sbar; y <= gc; y++) {
        this.xorPixel(xstart + x, y, 3);
      }
    }
  }

  /** Expand the indexed buffer into RGBA pixels using `palette`. */
  toRGBA(out: Uint8ClampedArray, palette: Palette): void {
    const { vram } = this;
    const c = palette.colors;
    for (let i = 0, o = 0; i < vram.length; i++, o += 4) {
      const [r, g, b] = c[vram[i]];
      out[o] = r;
      out[o + 1] = g;
      out[o + 2] = b;
      out[o + 3] = 255;
    }
  }
}
